import {
	ForwardedRef,
	useCallback,
	useContext,
	useEffect,
	useImperativeHandle,
	useState,
} from 'react';

import { FormikContext, getIn, useFormikContext } from 'formik';

interface UseEditableInputProps {
	fieldName: string;
	handleOnSave?: (value?: string, callback?: () => void) => void;
}

type FormikContextType = ReturnType<
	typeof useFormikContext<{ isUpdate: boolean } & Record<string, any>>
>;

export const useEditableDetail = (
	{ fieldName, handleOnSave }: UseEditableInputProps,
	ref?: ForwardedRef<any>
) => {
	let formik: FormikContextType | undefined;
	const formikContext = useContext(FormikContext);

	formik = formikContext
		? useFormikContext<{ isUpdate: boolean } & Record<string, any>>()
		: ({
				values: { isUpdate: false },
				initialValues: {},
				setFieldValue: () => {},
				submitForm: () => {},
			} as unknown as FormikContextType);

	const isUpdate = formik?.values?.isUpdate || false;

	const [editable, setEditable] = useState(!isUpdate);

	useImperativeHandle(ref, () => ({
		setEditable,
	}));

	useEffect(() => {
		if (!editable || !isUpdate || !formik) return;

		const initialValue = (formik?.initialValues as Record<string, any>)?.[
			fieldName
		];
		const currentValue = (formik?.values as Record<string, any>)?.[fieldName];

		if (initialValue === currentValue) {
			setEditable(false);
		}
	}, [formik?.initialValues]);

	const handleLabelClick = useCallback(() => {
		setEditable(true);
	}, []);

	const onCancel = useCallback(() => {
		setEditable(false);
		formik?.setFieldValue?.(
			fieldName,
			getIn(formik.initialValues, String(fieldName)) || ''
		);
	}, [formik, fieldName]);

	const onSave = useCallback(() => {
		if (
			formik?.initialValues?.[fieldName] === formik?.values?.[fieldName] &&
			!!formik?.values?.[fieldName]
		) {
			setEditable(false);
			return;
		}

		if (handleOnSave) {
			handleOnSave?.(formik?.values?.[fieldName], onCancel);
			return;
		}
		formik?.submitForm?.();
	}, [handleOnSave, formik, fieldName, onCancel]);

	return {
		formik,
		isUpdate,
		editable,
		setEditable,
		handleLabelClick,
		onCancel,
		onSave,
	};
};
